import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

export const SIDEBAR_BURST_MS = 720;

const SPARKS = [
  { x: 0, y: -46, delay: '0ms', color: '#7de0ff' },
  { x: 34, y: -32, delay: '30ms', color: '#0fb8ff' },
  { x: 48, y: 2, delay: '10ms', color: '#ffffff' },
  { x: 32, y: 36, delay: '50ms', color: '#3eccff' },
  { x: -2, y: 48, delay: '20ms', color: '#0084d6' },
  { x: -36, y: 30, delay: '40ms', color: '#b3f0ff' },
  { x: -50, y: -4, delay: '0ms', color: '#ffffff' },
  { x: -30, y: -34, delay: '25ms', color: '#0fb8ff' },
] as const;

/** Flash, anéis e faíscas. O conteúdo continua visível por cima. */
export function SidebarBurst({
  active,
  children,
  className,
  contentClassName,
}: {
  active: boolean;
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}) {
  return (
    <span className={cn('relative inline-flex', className)}>
      <span
        aria-hidden
        className={cn('sidebar-logo-burst pointer-events-none absolute inset-0', active && 'is-bursting')}
      >
        <span className="sidebar-logo-flash" />
        <span className="sidebar-logo-ring" />
        <span className="sidebar-logo-ring sidebar-logo-ring-late" />
        {SPARKS.map((spark) => (
          <span
            key={`${spark.x}:${spark.y}`}
            className="sidebar-logo-spark"
            style={{
              ['--spark-x' as string]: `${spark.x}px`,
              ['--spark-y' as string]: `${spark.y}px`,
              animationDelay: spark.delay,
              background: spark.color,
            }}
          />
        ))}
      </span>
      <span className={cn('relative z-10', active && 'sidebar-logo-pop', contentClassName)}>{children}</span>
    </span>
  );
}

/** Liga a explosão por um ciclo e desliga sozinha. O gatilho é estável. */
export function useSidebarBurst(): [boolean, () => void] {
  const [active, setActive] = useState(false);
  const burst = useCallback(() => setActive(true), []);

  useEffect(() => {
    if (!active) return;
    const timer = window.setTimeout(() => setActive(false), SIDEBAR_BURST_MS);
    return () => window.clearTimeout(timer);
  }, [active]);

  return [active, burst];
}
